import moment from 'moment'
import 'moment/locale/es'

moment.locale('es')

/*
 * Normaliza la respuesta de /api/reports/dashboard para la pantalla.
 * venta_meses alimenta la única gráfica (Mes, Trimestre, Año).
 * Hoy y Semana no tienen serie diaria en el API: no se inventa.
 */
const EMPTY = {
  ventas: 0,
  ordenes: 0,
  clientes: 0,
  efectivo: 0,
  tarjeta: 0,
  transferencia: 0,
  facturas: 0,
  recepcion: 0,
  proceso: 0,
  terminado: 0,
  finalizada: 0,
  alertas: [],
  topProductos: [],
  topServicios: [],
  meses: [],
}

function num(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function importeDe(item) {
  if (!item) return 0
  if (item.total != null) return num(item.total)
  if (item.importe != null) return num(item.importe)
  const precio = item._id && item._id.price
  if (precio != null) return num(precio) * (num(item.quantity) || 1)
  return 0
}

function alertRow(item) {
  return {
    id: item && item._id,
    name: (item && item.product && item.product.name) || 'Sin nombre',
    stock: num(item && item.stock),
    max: num(item && item.stock_max),
    agotado: num(item && item.stock) === 0,
  }
}

export function mapDashboard(data) {
  if (!data || data.status === false) return { ...EMPTY, alertas: [], topProductos: [], topServicios: [], meses: [] }

  const pagos = data.pagos || {}
  const servicios = data.servicios_pendientes || {}
  const alertas = [...(data.sin_stock || []), ...(data.stock_bajo || [])]
    .map(alertRow)
    .sort((a, b) => a.stock - b.stock)
    .slice(0, 3)

  const topProductos = (data.top_products || []).slice(0, 3).map((item) => ({
    id: item._id && (item._id.producto_id || item._id.name),
    name: (item._id && item._id.name) || 'Sin nombre',
    quantity: num(item.quantity),
    importe: importeDe(item),
  }))

  const topServicios = (data.top_services || []).slice(0, 3).map((item) => ({
    id: item._id && (item._id.service || item._id.name),
    name: (item._id && item._id.name) || 'Sin nombre',
    quantity: num(item.quantity),
    importe: importeDe(item),
  }))

  return {
    ventas: num(pagos.total_sales),
    ordenes: num(data.orders),
    clientes: num(data.clients),
    efectivo: num(pagos.efective),
    tarjeta: num(pagos.card),
    transferencia: num(pagos.transfer),
    facturas: num(pagos.totalFacturas),
    recepcion: num(servicios.recepcion),
    proceso: num(servicios.proceso),
    terminado: num(servicios.terminado),
    finalizada: num(servicios.finalizada),
    alertas,
    topProductos,
    topServicios,
    meses: (data.venta_meses || []).map((item) => ({
      year: item && item._id && item._id.year,
      month: item && item._id && item._id.month,
      total: num(item && item.total),
    })).filter((item) => item.year && item.month),
  }
}

const MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

function claveMes(year, month) {
  return year * 12 + (month - 1)
}

export function muestraGrafica(tipo) {
  return tipo === 'month' || tipo === 'quarter' || tipo === 'year'
}

/*
 * Serie real de venta_meses. Mes = últimos 12 meses.
 * Trimestre = últimos 4 trimestres. Año = últimos 5 años.
 */
export function serieIngreso(meses, tipo, now = new Date()) {
  const mapa = new Map()
  for (const item of meses || []) {
    mapa.set(claveMes(item.year, item.month), num(item.total))
  }
  const hoy = moment(now)
  if (tipo === 'year') {
    const year = hoy.year()
    const labels = []
    const values = []
    for (let y = year - 4; y <= year; y += 1) {
      labels.push(String(y))
      let suma = 0
      for (let m = 1; m <= 12; m += 1) suma += mapa.get(claveMes(y, m)) || 0
      values.push(suma)
    }
    return { labels, values }
  }
  if (tipo === 'quarter') {
    const labels = []
    const values = []
    let year = hoy.year()
    let start = Math.floor(hoy.month() / 3) * 3 + 1
    const slots = []
    for (let i = 0; i < 4; i += 1) {
      slots.unshift({ year, start })
      start -= 3
      if (start < 1) {
        start += 12
        year -= 1
      }
    }
    for (const slot of slots) {
      const trimestre = Math.floor((slot.start - 1) / 3) + 1
      labels.push(`T${trimestre} ${String(slot.year).slice(2)}`)
      let suma = 0
      for (let m = slot.start; m < slot.start + 3; m += 1) suma += mapa.get(claveMes(slot.year, m)) || 0
      values.push(suma)
    }
    return { labels, values }
  }
  const labels = []
  const values = []
  const inicio = hoy.clone().startOf('month').subtract(11, 'months')
  for (let i = 0; i < 12; i += 1) {
    const fecha = inicio.clone().add(i, 'months')
    labels.push(MES[fecha.month()])
    values.push(mapa.get(claveMes(fecha.year(), fecha.month() + 1)) || 0)
  }
  return { labels, values }
}

export function formatMoney(value) {
  const amount = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num(value))
  return `$${amount}`
}

export function ticketDe(ventas, ordenes) {
  const n = num(ordenes)
  if (n <= 0) return 0
  return num(ventas) / n
}

export function deltaMes(comparacion) {
  if (!comparacion) return null
  const actual = num(comparacion.actual)
  const anterior = num(comparacion.anterior)
  const diff = actual - anterior
  return {
    diff,
    pct: anterior > 0 ? (diff / anterior) * 100 : null,
  }
}

export function formatCount(value) {
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(num(value))
}

/*
 * El API solo acepta una unidad de Moment (startOf/endOf). Estos valores
 * ya funcionan sin cambiar el backend. isoWeek = lunes a domingo.
 */
export const PERIODOS = [
  { tipo: 'day', label: 'Hoy' },
  { tipo: 'isoWeek', label: 'Semana' },
  { tipo: 'month', label: 'Mes' },
  { tipo: 'quarter', label: 'Trim.' },
  { tipo: 'year', label: 'Año' },
]

function fechaLarga(date, withYear) {
  const mes = date.format('MMMM')
  return withYear
    ? `${date.format('D')} de ${mes} de ${date.format('YYYY')}`
    : `${date.format('D')} de ${mes}`
}

export function formatRango(inicio, fin) {
  const desde = moment(inicio).startOf('day')
  const hasta = moment(fin).startOf('day')
  if (!desde.isValid() || !hasta.isValid()) return ''
  if (desde.isAfter(hasta)) return formatRango(hasta, desde)
  if (desde.isSame(hasta, 'day')) return fechaLarga(desde, true)
  if (desde.isSame(hasta, 'month')) {
    return `${desde.format('D')}–${hasta.format('D')} de ${desde.format('MMMM')} de ${desde.format('YYYY')}`
  }
  if (desde.year() === hasta.year()) {
    return `${fechaLarga(desde, false)} – ${fechaLarga(hasta, true)}`
  }
  return `${fechaLarga(desde, true)} – ${fechaLarga(hasta, true)}`
}

export function periodRange(tipo, now = new Date()) {
  const start = moment(now).startOf(tipo)
  const end = moment(now).endOf(tipo)
  if (!start.isValid()) return ''
  if (tipo === 'year') return start.format('YYYY')
  return formatRango(start, end)
}

export function share(part, total) {
  const base = num(total)
  if (base <= 0) return 0
  return Math.max(0, Math.min(100, (num(part) / base) * 100))
}
